import {
  createRootRoute,
  createRoute,
  Navigate,
  Outlet,
  redirect,
  useLocation,
} from '@tanstack/react-router'
import { lazy } from 'react'
import { z } from 'zod'
import DefaultLayout from '@/components/Layouts/DefaultLayout'
import ExplorerOnlyLayout from '@/components/Layouts/ExplorerOnlyLayout'
import { getPreferredTradeMarginType } from '@/constants/marginTypes'
import Error404 from '@/pages/error/Error404'
import { PlexWebSocketProvider } from '@/providers/PlexWebSocketProvider'
import { ProtectedRoute } from '@/router/protected-route'
import { MarginType } from '@/types/Margin'
import { isBondswapAppCompany, isTradingProgramAppCompany } from '@/utils/appBranding'
import { isExplorerOnlyVariant } from '@/utils/appVariant'

// Lazy load components
const Bondswap = lazy(() => import('@/pages/bondswap'))
const Trade = lazy(() => import('@/pages/trade'))
const ViewOnlyMode = lazy(() => import('@/pages/view-only-mode'))
const Pool = lazy(() => import('@/pages/pool-liquidity'))
const Portfolio = lazy(() => import('@/pages/portfolio'))
const Explorer = lazy(() => import('@/pages/explorer'))
const Leaderboards = lazy(() => import('@/pages/leaderboards'))
const TradingStrategies = lazy(() => import('@/pages/trading-strategies'))
const StrategyBuilder = lazy(() => import('@/pages/strategy-builder'))
const YieldManagement = lazy(() => import('@/pages/yield/yield-management'))
const YieldCurve = lazy(() => import('@/pages/yield/yield-curve'))
const YieldPool = lazy(() => import('@/pages/yield/yield-pool'))
const YieldPoolCreationRestricted = lazy(() =>
  import('@/pages/yield/yield-pool/YieldPoolCreationRestricted').then((m) => ({
    default: m.YieldPoolCreationRestricted,
  })),
)
const YieldLiquidity = lazy(() => import('@/pages/yield/yield-liquidity'))
const SignInPage = lazy(() => import('@/pages/auth/SignInPage'))
const SignUpPage = lazy(() => import('@/pages/auth/SignUpPage'))
const ChallengePage = lazy(() => import('@/pages/challenge/ChallengePage'))
const ProvingGroundsPage = lazy(() => import('@/pages/proving-grounds/ProvingGroundsPage'))
const ProvingGroundsTermsPage = lazy(
  () => import('@/pages/proving-grounds/ProvingGroundsTermsPage'),
)
const ChallengeTermsPage = lazy(() => import('@/pages/challenge/ChallengeTermsPage'))
const ChallengePrivacyNoticePage = lazy(
  () => import('@/pages/challenge/ChallengePrivacyNoticePage'),
)
const ProvingGroundsPrivacyNoticePage = lazy(
  () => import('@/pages/proving-grounds/ProvingGroundsPrivacyNoticePage'),
)
const SendVerificationEmailPage = lazy(() => import('@/pages/auth/SendVerificationEmailPage'))
const RecoverPasswordPage = lazy(() => import('@/pages/auth/RecoverPasswordPage'))
const AuthActionPage = lazy(() => import('@/pages/auth/AuthAction'))
const PromoClaimPage = lazy(() => import('@/pages/promo/PromoClaimPage'))
const ApiKeyLoginPage = lazy(() => import('@/pages/auth/ApiKeyLoginPage'))
const ApiKeySessionClosedPage = lazy(() => import('@/pages/auth/ApiKeySessionClosedPage'))
const UserProfile = lazy(() => import('@/pages/user'))
const Administration = lazy(() => import('@/pages/administration'))
const AdministrationUserDetails = lazy(() => import('@/pages/administration/user-details'))
const AdministrationMarketMakerDetails = lazy(
  () => import('@/pages/administration/market-maker-details'),
)
const MarketMakerPage = lazy(() => import('@/pages/market-maker'))
const UserSelection = lazy(() => import('@/pages/useTypeSelection'))
const Bonds = lazy(() => import('@/pages/bonds'))
const OrderDetails = lazy(() => import('@/pages/order'))
const TradeDetails = lazy(() => import('@/pages/trade-details'))
const TransactionDetails = lazy(() => import('@/pages/transactions'))
const PositionDetails = lazy(() => import('@/pages/position-details'))
const MarketsOverview = lazy(() => import('@/pages/overview'))
const PoolDetails = lazy(() =>
  import('@/pages/pool-details/poolDetails').then((module) => ({ default: module.PoolDetails })),
)
const Lending = lazy(() => import('@/pages/lending'))

// Root route
const rootRoute = createRootRoute({
  component: RootComponent,
  notFoundComponent: isBondswapAppCompany() || isExplorerOnlyVariant ? HomepageRedirect : Error404,
})

function RootComponent() {
  const pathname = useLocation({ select: (location) => location.pathname })
  const isBondswapPage = pathname.replace(/\/$/, '') === '/bondswap' || isBondswapAppCompany()

  if (isBondswapPage) {
    return <Outlet />
  }

  return (
    <PlexWebSocketProvider>
      {isExplorerOnlyVariant ? (
        <ExplorerOnlyLayout />
      ) : (
        <ProtectedRoute>
          <DefaultLayout />
        </ProtectedRoute>
      )}
    </PlexWebSocketProvider>
  )
}

function HomepageRedirect() {
  return <Navigate to="/" replace />
}

// Bondswap renders at the root without changing the browser URL.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    if (isBondswapAppCompany()) return

    throw redirect({
      to: '/trade',
      search: { marginType: getPreferredTradeMarginType() },
    })
  },
  component: Bondswap,
})

const bondswapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bondswap',
  component: Bondswap,
})

const explorerOnlyIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Explorer,
})

// Overview route
const marketsOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/overview',
  component: MarketsOverview,
})

const parseTradeSearch = (search: unknown) =>
  z
    .object({
      marginType: z
        .enum([MarginType.CASH, MarginType.ISOLATED])
        .catch(getPreferredTradeMarginType()),
      positionId: z.string().optional(),
    })
    .parse(search)

// Trade route
const tradeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trade',
  validateSearch: parseTradeSearch,
  component: Trade,
})

// Trade route with orderBookId parameter
const tradeWithOrderBookRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trade/$orderBookId',
  validateSearch: parseTradeSearch,
  component: Trade,
})

// Public view-only market route
const viewOnlyModeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/viewOnlyMode',
  component: ViewOnlyMode,
})

// Pool route
const poolRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pool',
  component: Pool,
})

// Pool route with orderBookId parameter
const poolWithOrderBookRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pool/$orderBookId',
  component: Pool,
})

// Pool details route
const poolDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pool-details/$poolID',
  component: PoolDetails,
})

// Portfolio route
const portfolioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portfolio',
  component: Portfolio,
})

// Explorer route
const explorerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/explorer',
  component: Explorer,
})

const parseLeaderboardsSearch = (search: unknown) =>
  z
    .object({
      challengeId: z.string().optional(),
    })
    .parse(search)

// Leaderboards route
const leaderboardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leaderboards',
  validateSearch: parseLeaderboardsSearch,
  component: Leaderboards,
})

// Trading strategies route
const tradingStrategiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trading-strategies',
  component: TradingStrategies,
})

const strategyBuilderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/strategy-builder',
  component: StrategyBuilder,
})

// Yield route
const yieldRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/yield',
  component: YieldManagement,
})

// Yield curve route
const yieldCurveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/yield-curve',
  component: YieldCurve,
})

// Yield pool route
const yieldPoolRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/yield-pool',
  component: YieldPool,
})

// Yield pool restricted route
const yieldPoolRestrictedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/yield-pool-restricted',
  component: YieldPoolCreationRestricted,
})

// Yield liquidity route
const yieldLiquidityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/yield-liquidity',
  component: YieldLiquidity,
})

// Bonds route
const bondsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bonds/$orderBookId',
  component: Bonds,
})

// Order details route
const orderDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/order-details/$orderBookID/$orderID',
  component: OrderDetails,
})

// Trade details route
const tradeDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trade-details/$orderBookID/$tradeId',
  component: TradeDetails,
})

// Position details route
const positionDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/position-details/$accountId',
  component: PositionDetails,
})

// Transaction details route
const transactionDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transaction/$txId/$txType',
  component: TransactionDetails,
})

// Lending route
const lendingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lending',
  component: Lending,
})

// User routes
const userProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/user/profile',
  component: UserProfile,
})

const userAffiliatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/user/affiliates',
  component: UserProfile,
})

const userSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/user/settings',
  component: UserProfile,
})

const userApiAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/user/api-access',
  component: UserProfile,
})

const userSelectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/userSelection',
  component: UserSelection,
})

// Administration route
const administrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/administration',
  component: Administration,
})

const administrationUserDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/administration/users/$userId',
  component: AdministrationUserDetails,
})

const administrationMarketMakerDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/administration/market-makers/$marketMakerId',
  component: AdministrationMarketMakerDetails,
})

const marketMakerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/market-maker',
  component: MarketMakerPage,
})

// Help route
const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/help',
  component: UserProfile,
})

// Auth routes (back to using root route)
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/sign-in',
  component: SignInPage,
})

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/sign-up',
  component: SignUpPage,
})

const challengeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/challenge',
  component: ChallengePage,
})

const provingGroundsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/proving-grounds',
  component: ProvingGroundsPage,
})

const challengeTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/challenge/terms',
  component: ChallengeTermsPage,
})

const provingGroundsTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/proving-grounds/terms',
  component: ProvingGroundsTermsPage,
})

const challengePrivacyNoticeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/challenge/privacy-notice',
  component: ChallengePrivacyNoticePage,
})

const provingGroundsPrivacyNoticeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/proving-grounds/privacy-notice',
  component: ProvingGroundsPrivacyNoticePage,
})

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/verify-email',
  component: SendVerificationEmailPage,
})

const recoverPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/recover',
  component: RecoverPasswordPage,
})

const authActionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/action',
  component: AuthActionPage,
})

const promoClaimRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/promo/claim/$token',
  component: PromoClaimPage,
})

const apiKeyLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: ApiKeyLoginPage,
})

const apiKeySessionClosedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/session-closed',
  component: ApiKeySessionClosedPage,
})

const tradingProgramRoutes = isTradingProgramAppCompany()
  ? [
      challengeRoute,
      provingGroundsRoute,
      challengeTermsRoute,
      provingGroundsTermsRoute,
      challengePrivacyNoticeRoute,
      provingGroundsPrivacyNoticeRoute,
    ]
  : []

// Create route tree with both protected and auth routes
export const routeTree = isBondswapAppCompany()
  ? rootRoute.addChildren([indexRoute])
  : isExplorerOnlyVariant
    ? rootRoute.addChildren([
        explorerOnlyIndexRoute,
        bondswapRoute,
        explorerRoute,
        transactionDetailsRoute,
      ])
    : rootRoute.addChildren([
        indexRoute,
        bondswapRoute,
        marketsOverviewRoute,
        tradeRoute,
        tradeWithOrderBookRoute,
        viewOnlyModeRoute,
        poolRoute,
        poolWithOrderBookRoute,
        poolDetailsRoute,
        portfolioRoute,
        leaderboardsRoute,
        explorerRoute,
        tradingStrategiesRoute,
        strategyBuilderRoute,
        yieldRoute,
        yieldCurveRoute,
        yieldPoolRoute,
        yieldPoolRestrictedRoute,
        yieldLiquidityRoute,
        bondsRoute,
        orderDetailsRoute,
        tradeDetailsRoute,
        positionDetailsRoute,
        transactionDetailsRoute,
        userProfileRoute,
        userSettingsRoute,
        userAffiliatesRoute,
        userApiAccessRoute,
        userSelectionRoute,
        administrationRoute,
        administrationUserDetailsRoute,
        administrationMarketMakerDetailsRoute,
        helpRoute,
        lendingRoute,
        marketMakerRoute,
        // Auth routes as separate children
        signInRoute,
        signUpRoute,
        ...tradingProgramRoutes,
        verifyEmailRoute,
        recoverPasswordRoute,
        authActionRoute,
        promoClaimRoute,
        apiKeyLoginRoute,
        apiKeySessionClosedRoute,
      ])
